import { Chip, List, MenuItem, Paper, Popover, Typography } from "@mui/material";
import { Cancel, CheckCircle, ExpandMore } from "@mui/icons-material";

import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setCurrentStageName } from "../../reducers/rtfoSlice";
import { useSocket } from "../../context/useSocket";
import { isRTFOAdminUser, isRTFOSuperUser } from "./rtfoConfig";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOFracModelDataTable = ({ currentRTFO, darkMode, userEmail }) => {

    // STYLES
    const classes = useStyles();
    const dispatch = useDispatch();
    const units = currentRTFO?.settings?.units || 'metric';


    const rows = useMemo(() => {
      if (!currentRTFO?.stages?.length > 0) return [];
      let stages = [];

      currentRTFO?.stages?.forEach(stageName => {
        const stage = currentRTFO?.stageData?.[stageName];
        const aSqrtK = stage?.fracModel?.aSqrtK;
        const fracArea = stage?.fracModel?.fracArea;
        const fracLength = stage?.fracModel?.halfLength || (fracArea / (currentRTFO?.settings?.fracHeight * 2)) || 300;

        if (!isNaN(aSqrtK) && !isNaN(fracArea)) {
          stages.push({
            id: stageName,
            stageStartTimestamp: stage?.stageStartTimestamp,
            stageName,
            wellName: stage?.wellName,
            wellUwi: stage?.wellUwi,
            fracModelType: stage?.fracModel?.model === 'normal' ? 'Advanced' : 'Simplified',
            hasISIP: stage?.metrics?.isip > 0,
            hasFalloff: stage?.fracModel?.falloffData?.length > 0,
            stageNumber: String(stage?.stageNumber),  
            aSqrtK: parseFloat((aSqrtK * (units === 'field' ? rtfoConversionFactors.fracArea : 1))?.toFixed(2)) || 0,
            fracArea: parseFloat((fracArea * (units === 'field' ? rtfoConversionFactors.fracArea : 1))?.toFixed(2)) || 0,
            halfLength: parseFloat((fracLength * (units === 'field' ? rtfoConversionFactors.halfLength : 1))?.toFixed(2)) || 0,
            dPPerLogCycle: parseFloat((stage?.fracModel?.dPPerLogCycle * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0
          });
        };
      });

      stages = stages.sort((a, b) => b.stageStartTimestamp - a.stageStartTimestamp);

      return stages;
    }, [currentRTFO?.settings?.fracHeight, currentRTFO?.stageData, currentRTFO?.stages, units]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 175,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well Name',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellUwi',
        headerName: units === 'field' ? 'API Number' : 'Well UWI',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage Number',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'fracModelType',
        headerName: 'Model Type',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        // type: 'string'
        renderCell: (params) => (isRTFOAdminUser(userEmail) || isRTFOSuperUser(userEmail)) ? (
          <DropdownChip options={['Advanced', 'Simplified']} label={params.value} stageName={params?.id} fracModelType={params?.value} padName={currentRTFO?.name} />
        ) : (<Chip label={params.value} size="small" style={{fontSize: 12}} />),
      },
      {
        field: 'hasISIP',
        headerName: 'ISIP',
        width: 50,
        editable: false,
        sortable: true,
        filterable: true,
        // type: 'string'
        renderCell: (params) => params.value ? <CheckCircle style={{color: "green", fontSize: '16px'}} /> : <Cancel style={{color: "tomato", fontSize: '16px'}} />,
      },
      {
        field: 'hasFalloff',
        headerName: 'Falloff',
        width: 50,
        editable: false,
        sortable: true,
        filterable: true,
        // type: 'string'
        renderCell: (params) => params.value ? <CheckCircle style={{color: "green", fontSize: '16px'}} /> : <Cancel style={{color: "tomato", fontSize: '16px'}} />,
      },
      {
        field: 'aSqrtK',
        headerName: `A*sqrt(k) (${units === 'field' ? 'ft2' : 'm2'}*mD^0.5)`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fracArea',
        headerName: `Frac Area (${units === 'field' ? 'ft2' : 'm2'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'halfLength',
        headerName: `Half Length (${units === 'field' ? 'ft' : 'm'})`,
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'dPPerLogCycle',
        headerName: `dP / Log Cycle (${units === 'field' ? 'psi' : 'MPa'}/log cycle)`,
        width: 200,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      }
    ];


    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: CustomToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            }
          }}
          style={{fontSize: '12px'}}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
          onRowClick={params => (!currentRTFO?.isLoadingData && dispatch(setCurrentStageName(params?.row?.stageName)))}
          density="compact"
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
      </GridToolbarContainer>
    );
  };
};

const DropdownChip = ({ options, label, stageName, fracModelType, padName }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const { socket } = useSocket();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'dropdown-popover' : undefined;

  return (
    <>
      <Chip
        size="small"
        label={label}
        onClick={handleClick}
        onDelete={handleClick} // Optional: Trigger dropdown on delete icon click
        deleteIcon={<ExpandMore />}
        style={{fontSize: 12}}
      />
      {/* <IconButton onClick={handleClick} size="small">
        <ExpandMore />
      </IconButton> */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List>
          {options.map((option, index) => (
            <MenuItem key={index} selected={option === fracModelType} onClick={() => handleTypeChange(option, stageName, fracModelType, padName)}><Typography>{option}</Typography></MenuItem>
          ))}

        </List>
      </Popover>
    </>
  );

  function handleTypeChange(option, stageName, previousOption, padName) {
    if (option !== previousOption) {
      console.log(`Setting ${stageName} to ${option?.toLowerCase()} frac model`);

      socket?.send(JSON.stringify({
        messageType: 'changeFracModelType',
        padName,
        stageName,
        newType: option?.toLowerCase()
      }));
    }
    handleClose();
  }
};